import { useEffect, useRef } from "react";
import { OpenSheetMusicDisplay } from "opensheetmusicdisplay";
import QuestionLayout from "./QuestionLayout";

/* ───────────────────────────────────────────── */

export default function TimeSignatureTopNumberWidget({
  id,
  config,
  value = {},
  onChange,
  mode,
  feedback
}) {
  const containerRef = useRef(null);
  const osmdRef = useRef(null);
  const overlayRef = useRef(null);
  const initializedRef = useRef(false);
  const valueRef = useRef(value);

  /* ───────── KEEP VALUE REF IN SYNC ───────── */

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  /* ───────── OSMD INIT (ONCE, NO RESIZE EVER) ───────── */

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const osmd = new OpenSheetMusicDisplay(containerRef.current, {
      drawTitle: false,
      drawPartNames: false,
      drawComposer: false,
      drawMeasureNumbers: false,
      // drawingParameters: "compacttight",
      autoResize: false // 🔒 HARD LOCK
    });

    osmdRef.current = osmd;

    (async () => {
      await osmd.load(config.musicXML);
      // osmd.EngravingRules.RenderXMeasuresPerLineAkaSystem = 5;
      osmd.EngravingRules.RenderXMeasuresPerLineAkaSystem = 5;
      osmd.render();

      // wait for layout + DOM paint
      await new Promise(r =>
        requestAnimationFrame(() =>
          requestAnimationFrame(r)
        )
      );

      ensureOverlay();
      locateTimeSignatureSlots();
    })();
  }, []);

  /* ───────── REBUILD ON REVIEW / FEEDBACK ONLY ───────── */

  useEffect(() => {
    if (!initializedRef.current) return;
    clearInputs();
    locateTimeSignatureSlots();
  }, [mode, feedback]);

  /* ───────── OVERLAY LAYER ───────── */

  function ensureOverlay() {
    const parent = containerRef.current;
    if (!parent) return;

    parent.style.position = "relative";

    let layer = parent.querySelector("#tsig-input-overlay");
    if (!layer) {
      layer = document.createElement("div");
      layer.id = "tsig-input-overlay";
      layer.style.position = "absolute";
      layer.style.left = "0";
      layer.style.top = "0";
      layer.style.width = "100%";
      layer.style.height = "100%";
      layer.style.pointerEvents = "none";
      parent.appendChild(layer);
    }

    overlayRef.current = layer;
  }

  function clearInputs() {
    if (overlayRef.current) {
      overlayRef.current.innerHTML = "";
    }
  }

  /* ───────── FIND NUMERATOR GLYPHS (ONCE PER RENDER) ───────── */

  function locateTimeSignatureSlots() {
    const overlay = overlayRef.current;
    if (!overlay) return;

    overlay.innerHTML = "";

    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    const svgRect = svg.getBoundingClientRect();

    const tsGroups = Array.from(
      svg.querySelectorAll(
        'g[class*="time"], g[class*="Time"], g[class*="timesignature"]'
      )
    );

    const inputMode = config.timeSignatureInputMode ?? "top";
    const bothNumbers = inputMode === "both";

    let slotIndex = 0;

    tsGroups.forEach(g => {
      const children = Array.from(g.children)
        .map(el => ({
          el,
          rect: el.getBoundingClientRect()
        }))
        .filter(o => o.rect.width > 4 && o.rect.height > 8);

      if (children.length < 2) return;

      // OSMD renders multi-digit time-signature numbers (e.g. 12)
      // as separate SVG children. Split the children into the two
      // vertical rows instead of assuming one child per number.
      const byCenterY = [...children].sort(
        (a, b) =>
          (a.rect.top + a.rect.bottom) / 2 -
          (b.rect.top + b.rect.bottom) / 2
      );

      let splitIndex = 0;
      let largestGap = -Infinity;
      for (let i = 0; i < byCenterY.length - 1; i++) {
        const currentCenter =
          (byCenterY[i].rect.top + byCenterY[i].rect.bottom) / 2;
        const nextCenter =
          (byCenterY[i + 1].rect.top + byCenterY[i + 1].rect.bottom) / 2;
        const gap = nextCenter - currentCenter;

        if (gap > largestGap) {
          largestGap = gap;
          splitIndex = i;
        }
      }

      const mergeGlyphs = items => {
        const left = Math.min(...items.map(item => item.rect.left));
        const right = Math.max(...items.map(item => item.rect.right));
        const top = Math.min(...items.map(item => item.rect.top));
        const bottom = Math.max(...items.map(item => item.rect.bottom));

        return {
          elements: items.map(item => item.el),
          rect: {
            left,
            right,
            top,
            bottom,
            width: right - left,
            height: bottom - top
          }
        };
      };

      const numerator = mergeGlyphs(byCenterY.slice(0, splitIndex + 1));
      const denominator = mergeGlyphs(byCenterY.slice(splitIndex + 1));

      // Use the full time-signature column as the horizontal anchor so
      // a two-digit numerator is centered over the denominator exactly
      // like the one-digit cases.
      const columnLeft = Math.min(numerator.rect.left, denominator.rect.left);
      const columnRight = Math.max(numerator.rect.right, denominator.rect.right);
      const columnRect = {
        left: columnLeft,
        right: columnRight,
        width: columnRight - columnLeft
      };

      const slotBounds = findVisualSlotBounds({
        svg,
        timeSigGroup: g,
        anchorRect: columnRect,
        glyphRect: numerator.rect
      });

      createTimeSigInput({
        glyph: numerator,
        alignRect: columnRect,
        svgRect,
        slotBounds,
        slotIndex,
        field: bothNumbers ? "top" : null
      });

      numerator.elements.forEach(el => {
        el.style.visibility = "hidden";
      });

      if (bothNumbers) {
        createTimeSigInput({
          glyph: denominator,
          alignRect: columnRect,
          svgRect,
          slotBounds,
          slotIndex,
          field: "bottom",
          bot: true,
        });

        denominator.elements.forEach(el => {
          el.style.visibility = "hidden";
        });
      }

      slotIndex++;
    });
  }

  function findVisualSlotBounds({ svg, timeSigGroup, anchorRect, glyphRect }) {
    const anchor = anchorRect ?? glyphRect;
    const staffCenterY = (glyphRect.top + glyphRect.bottom) / 2;

    // OSMD/VexFlow class names are not stable enough to identify notes and
    // barlines. Inspect actual rendered SVG primitives instead. This makes
    // the input fit the real visual gap around the printed time signature.
    const primitives = Array.from(
      svg.querySelectorAll("path, rect, line, polygon, polyline, ellipse, circle, text, use")
    )
      .filter(el => !timeSigGroup.contains(el))
      .map(el => ({ el, rect: el.getBoundingClientRect() }))
      .filter(({ rect }) =>
        Number.isFinite(rect.left) &&
        Number.isFinite(rect.right) &&
        rect.width > 0 &&
        rect.height > 0
      );

    const overlapsStaffBand = rect => {
      const top = staffCenterY - 32;
      const bottom = staffCenterY + 32;
      return rect.bottom >= top && rect.top <= bottom;
    };

    const isLongStaffLine = rect => rect.width > 80 && rect.height <= 4;

    // A barline is a narrow, tall primitive immediately to the left of the
    // time signature. Restrict the search distance so note stems farther back
    // in the previous measure cannot become the left boundary.
    const leftCandidates = primitives
      .filter(({ rect }) =>
        overlapsStaffBand(rect) &&
        !isLongStaffLine(rect) &&
        rect.width <= 8 &&
        rect.height >= 20 &&
        rect.right <= anchor.left + 1 &&
        anchor.left - rect.right <= 55
      )
      .sort((a, b) => b.rect.right - a.rect.right);

    // The first rendered primitive to the right is a reliable hard stop for
    // the first note/rest (or its stem/beam). Ignore long staff lines so they
    // don't collapse the available slot to zero.
    const rightCandidates = primitives
      .filter(({ rect }) =>
        overlapsStaffBand(rect) &&
        !isLongStaffLine(rect) &&
        rect.left >= anchor.right - 1 &&
        rect.left - anchor.right <= 90
      )
      .sort((a, b) => a.rect.left - b.rect.left);

    return {
      left: leftCandidates[0]?.rect.right ?? null,
      right: rightCandidates[0]?.rect.left ?? null
    };
  }

  function createTimeSigInput({
    glyph,
    alignRect,
    svgRect,
    slotBounds,
    slotIndex,
    field,
    bot = false
  }) {
    const r = glyph.rect;
    const isReview = mode === "review";

    const part = field
      ? feedback?.parts?.[slotIndex]?.[field]
      : feedback?.parts?.[slotIndex];

    const input = document.createElement("input");

    input.value = field
      ? valueRef.current?.[slotIndex]?.[field] ?? ""
      : valueRef.current?.[slotIndex] ?? "";

    input.disabled = isReview;
    input.type = "text";
    input.inputMode = "numeric";
    input.pattern = "[0-9]*";
    input.maxLength = 2;
    input.autocomplete = "off";
    input.spellcheck = false;

    const desiredInputWidth = Number(config.timeSignatureInputWidth ?? 22);
    const minimumInputWidth = Number(config.timeSignatureInputMinWidth ?? 18);
    const sideGap = Number(config.timeSignatureInputSideGap ?? 2);
    const anchor = alignRect ?? r;
    const anchorCenter = (anchor.left + anchor.right) / 2 - svgRect.left;

    const hardLeft = Number.isFinite(slotBounds?.left)
      ? slotBounds.left - svgRect.left + sideGap
      : null;
    const hardRight = Number.isFinite(slotBounds?.right)
      ? slotBounds.right - svgRect.left - sideGap
      : null;

    let inputWidth = desiredInputWidth;
    let inputLeft = anchorCenter - inputWidth / 2;

    if (hardLeft != null && hardRight != null && hardRight > hardLeft) {
      const available = hardRight - hardLeft;
      inputWidth = Math.min(desiredInputWidth, available);
      inputWidth = Math.max(Math.min(minimumInputWidth, available), inputWidth);
      inputLeft = Math.min(
        Math.max(inputLeft, hardLeft),
        hardRight - inputWidth
      );
    } else if (hardLeft != null) {
      inputLeft = Math.max(inputLeft, hardLeft);
    } else if (hardRight != null) {
      inputLeft = Math.min(inputLeft, hardRight - inputWidth);
    }

    input.style.position = "absolute";
    input.style.left = `${inputLeft}px`;
    input.style.top = `${r.top - svgRect.top - 5 + (bot ? 0 : 0) + 5}px`;
    input.style.width = `${inputWidth}px`;
    input.style.height = `${r.height}px`;
    input.style.boxSizing = "border-box";
    input.style.margin = "0";
    input.style.padding = "0 1px";
    input.style.border = "1px solid #777";
    input.style.borderRadius = "3px";
    input.style.backgroundColor = "#fff";
    input.style.color = "#111";
    input.style.fontSize = "16px";
    input.style.lineHeight = "1";
    input.style.textAlign = "center";
    input.style.pointerEvents = "auto";
    input.style.appearance = "none";
    input.style.webkitAppearance = "none";

    if (isReview && part) {
      input.style.border = "2px solid";
      input.style.borderRadius = "4px";
      input.style.borderColor = part.correct
        ? "#2e7d32"
        : "#c62828";
      input.style.backgroundColor = part.correct
        ? "#e8f5e9"
        : "#fdecea";
    }

    input.addEventListener("input", e => {
      if (isReview) return;

      const raw = e.target.value;
      const parsed = raw === "" ? "" : Number(raw);

      if (field) {
        onChange({
          ...(valueRef.current || {}),
          [slotIndex]: {
            ...(valueRef.current?.[slotIndex] || {}),
            [field]: parsed
          }
        });
      } else {
        onChange({
          ...(valueRef.current || {}),
          [slotIndex]: parsed
        });
      }
    });

    overlayRef.current.appendChild(input);
  }

  /* ───────── RENDER ───────── */

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
      legendImage={config.legendImage}
    >
      <div
        style={{
          width: config.osmdWidth ?? 1400,
          maxWidth: "none"
        }}
      >
        <div
          ref={containerRef}
          style={{
            width: "100%",
            maxWidth: "none"
          }}
        />
      </div>
    </QuestionLayout>
  );
}
